import { RouteConfigurationServiceDomain } from './domain';

export class CallbackURLSanitizer {
    /**
     * Base URL used for parsing relative URLs with the URL API.
     * This allows us to leverage native URL parsing for both absolute and relative URLs.
     */
    private readonly BASE_URL = 'https://placeholder.com';

    /**
     * Determines whether a callbackURL for OAuth Authorization Code Flow is safe to redirect to after authentication.
     * Validates only the pathname portion against the allowlist, ignoring query parameters and hash fragments.
     * This ensures query parameters are preserved while maintaining security through strict path validation.
     *
     * @param callbackURL - The callback URL to validate (can include query params and hash)
     * @returns True if the callback URL's pathname is safe to redirect to
     */
    public isSafeCallbackUrl(callbackURL: string | null): callbackURL is string {
        if (!callbackURL) {
            return false;
        }

        const pathname = this.extractPathname(callbackURL);

        /**
         * Cannot redirect to the signin bounce page because it will cause an infinite loop.
         */
        if (pathname === RouteConfigurationServiceDomain.ApplicationRoute.AUTH_SIGNIN_BOUNCE) {
            return false;
        }

        const isAllowedExternal =
            RouteConfigurationServiceDomain.ALLOWED_EXTERNAL_CALLBACK_URLS.includes(
                pathname as RouteConfigurationServiceDomain.ExternalRoute
            ) ||
            // Cover terminating slash case
            RouteConfigurationServiceDomain.ALLOWED_EXTERNAL_CALLBACK_URLS.includes(
                `${pathname}/` as RouteConfigurationServiceDomain.ExternalRoute
            ) ||
            // Cover non-terminating slash case
            RouteConfigurationServiceDomain.ALLOWED_EXTERNAL_CALLBACK_URLS.includes(
                pathname.replace(/\/$/, '') as RouteConfigurationServiceDomain.ExternalRoute
            );
        const isInternalRoute = pathname.startsWith('/homeloans/mortgage/');

        return isAllowedExternal || isInternalRoute;
    }

    /**
     * Returns a safe callback URL for OAuth Authorization Code Flow, given an unknown callback URL, handling default fallbacks
     * @param callbackURL - The unknown callback URL.
     * @returns The safe callback URL.
     */
    public getSafeCallbackUrl(callbackURL: string | null): string {
        if (!this.isSafeCallbackUrl(callbackURL)) {
            return RouteConfigurationServiceDomain.ApplicationRoute.DASHBOARD_BOUNCE;
        }
        return this.getRelativePath(callbackURL);
    }

    /**
     * Parses a callback URL (absolute or relative) into a URL object.
     * @param callbackURL - The callback URL to parse.
     * @returns A URL object.
     */
    private parseURL(callbackURL: string): URL {
        try {
            // Try parsing as absolute URL first
            return new URL(callbackURL);
        } catch {
            // If it fails, it's likely a relative URL, parse with base URL
            return new URL(callbackURL, this.BASE_URL);
        }
    }

    /**
     * Returns the relative path of a callback URL, handling both relative and full URLs.
     * Preserves query parameters and hash fragments.
     * @param callbackURL - The callback URL to get the relative path of.
     * @returns The relative path of the callback URL with query parameters and hash fragments.
     */
    public getRelativePath(callbackURL: string): string {
        const url = this.parseURL(callbackURL);
        return `${url.pathname}${url.search}${url.hash}`;
    }

    /**
     * Extracts only the pathname from a callback URL, stripping query parameters and hash fragments.
     * Used for allowlist validation while preserving the full URL for actual redirects.
     * @param callbackURL - The callback URL to extract the pathname from.
     * @returns The pathname only, without query parameters or hash fragments.
     */
    private extractPathname(callbackURL: string): string {
        const url = this.parseURL(callbackURL);
        return url.pathname;
    }
}
